/**
 * Service: reviewService.js
 * Gerencia avaliações (reviews) de barbearias
 */

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { createAuditLog } from './auditService';

const COLLECTION = 'reviews';

/**
 * Criar uma nova avaliação
 * Validações:
 * - Usuário pode avaliar apenas agendamentos completados
 * - Não pode avaliar o mesmo agendamento duas vezes
 */
export const createReview = async (reviewData, appointmentData = null) => {
  try {
    // Validação: Verificar se o agendamento foi completado
    if (appointmentData && appointmentData.status !== 'completed') {
      return {
        success: false,
        error: 'Você só pode avaliar após o serviço ser concluído'
      };
    }

    // Validação: Verificar duplicatas
    if (reviewData.appointmentId) {
      const hasReviewed = await hasUserReviewed(reviewData.userId, reviewData.appointmentId);
      if (hasReviewed.success && hasReviewed.data) {
        return {
          success: false,
          error: 'Você já avaliou este agendamento'
        };
      }
    }

    const docRef = await addDoc(collection(db, COLLECTION), {
      ...reviewData,
      createdAt: serverTimestamp(),
      isModerated: false, // Para moderação futura
      moderatedBy: null,
      moderatedAt: null,
    });

    // Registrar log de auditoria
    const currentUser = auth.currentUser;
    if (currentUser) {
      await createAuditLog({
        action: 'create_review',
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userRole: 'user',
        barbershopId: reviewData.barbershopId,
        details: {
          reviewId: docRef.id,
          rating: reviewData.rating,
          appointmentId: reviewData.appointmentId
        }
      });
    }

    return {
      success: true,
      data: { id: docRef.id, ...reviewData }
    };
  } catch (error) {
    console.error('Erro ao criar avaliação:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar avaliações de uma barbearia
 */
export const getReviewsByBarbershop = async (barbershopId) => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('barbershopId', '==', barbershopId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const reviews = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: reviews };
  } catch (error) {
    console.error('Erro ao buscar avaliações:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verificar se usuário já avaliou um agendamento específico
 */
export const hasUserReviewed = async (userId, appointmentId) => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('appointmentId', '==', appointmentId)
    );
    
    const snapshot = await getDocs(q);
    return { success: true, data: !snapshot.empty };
  } catch (error) {
    console.error('Erro ao verificar avaliação:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calcular média de avaliações de uma barbearia
 */
export const calculateAverageRating = async (barbershopId) => {
  try {
    const result = await getReviewsByBarbershop(barbershopId);
    
    if (!result.success || result.data.length === 0) {
      return { success: true, data: { average: 0, total: 0 } };
    }

    const reviews = result.data;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    const average = sum / reviews.length;

    return {
      success: true,
      data: {
        average: Math.round(average * 10) / 10, // Arredonda para 1 casa decimal
        total: reviews.length
      }
    };
  } catch (error) {
    console.error('Erro ao calcular média:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Moderar avaliação (Admin)
 * @param {string} reviewId - ID da avaliação
 * @param {boolean} isApproved - Se foi aprovada ou removida
 * @param {string} adminId - ID do admin que moderou
 */
export const moderateReview = async (reviewId, isApproved, adminId) => {
  try {
    const reviewRef = doc(db, COLLECTION, reviewId);
    
    await updateDoc(reviewRef, {
      isModerated: true,
      isApproved: isApproved,
      moderatedBy: adminId,
      moderatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Erro ao moderar avaliação:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar avaliação (Admin ou Autor)
 */
export const deleteReview = async (reviewId) => {
  try {
    const reviewRef = doc(db, COLLECTION, reviewId);
    
    // Buscar dados da review antes de deletar para o log
    const reviewSnap = await getDoc(reviewRef);
    const reviewData = reviewSnap.exists() ? reviewSnap.data() : null;
    
    await deleteDoc(reviewRef);
    
    // Registrar log de auditoria
    const currentUser = auth.currentUser;
    if (currentUser && reviewData) {
      await createAuditLog({
        action: 'delete_review',
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userRole: 'admin', // Pode ser admin ou owner
        barbershopId: reviewData.barbershopId,
        details: {
          reviewId: reviewId,
          rating: reviewData.rating,
          deletedReviewUserId: reviewData.userId
        }
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Erro ao deletar avaliação:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Responder a uma avaliação (dono da barbearia)
 */
export const replyToReview = async (reviewId, ownerReply) => {
  try {
    const reviewRef = doc(db, COLLECTION, reviewId);
    await updateDoc(reviewRef, {
      ownerReply,
      repliedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Erro ao responder avaliação:', error);
    return { success: false, error: error.message };
  }
};
